import { useMemo, useState, useEffect } from 'react';
import { useTabs } from '../../../hooks/useTabs/useTabs';
import { usePortalsInfo } from '../../../Context/usePortalsInfo';
import { useVisibilityContext } from './useVisibilityContext';

/**
 * @param {CommandPalettePlugin[]} plugins
 * @param {string} searchContent
 */
export const usePlugins = (paneId, groupId, plugins, searchContent) => {
  const [areInitilalized, setAreInitilalized] = useState(false);
  const [areFiltered, setAreFiltered] = useState(false);
  const [initilizedPlugins, setInitilizedPlugins] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [visiblePlugins, setVisiblePlugins] = useState([]);

  const [hasInitError, setHasInitError] = useState(false);
  const [hasFilterError, setHasFilterError] = useState(false);

  const {
    getVisibility,
    isLoaded: isVisibilityLoaded,
    hasError: hasVisibilityError,
    reload,
  } = useVisibilityContext(paneId, groupId);

  const areLoaded = useMemo(() => {
    if (hasVisibilityError || hasInitError || hasFilterError) {
      return true;
    }
    return isVisibilityLoaded && areInitilalized && areFiltered;
  }, [
    hasVisibilityError,
    hasInitError,
    hasFilterError,
    isVisibilityLoaded,
    areInitilalized,
    areFiltered,
  ]);

  const hasError = useMemo(
    () =>
      (isVisibilityLoaded && hasInitError) ||
      (areInitilalized && hasFilterError) ||
      (areFiltered && hasVisibilityError),
    [
      hasInitError,
      hasFilterError,
      hasVisibilityError,
      isVisibilityLoaded,
      areInitilalized,
      areFiltered,
    ],
  );

  const isAnyPluginVisible = useMemo(
    () =>
      !isVisibilityLoaded || hasVisibilityError || visiblePlugins.length > 0,
    [hasVisibilityError, isVisibilityLoaded, visiblePlugins.length],
  );

  const tabsHookResults = useTabs();
  const portalsHookResults = usePortalsInfo();

  const gwContext = useMemo(
    () => ({
      tabs: tabsHookResults,
      portals: portalsHookResults,
    }),
    [tabsHookResults, portalsHookResults],
  );

  useEffect(() => {
    setVisiblePlugins(
      isVisibilityLoaded
        ? plugins.filter((plugin) => getVisibility(plugin.id))
        : [],
    );
  }, [getVisibility, hasVisibilityError, isVisibilityLoaded, plugins]);

  useEffect(() => {
    setAreInitilalized(false);

    const initPlugins = async () => {
      setHasInitError(false);
      const pluginsResult = await Promise.all(
        visiblePlugins.map((plugin) => {
          return plugin.getResults(gwContext).catch(() => {
            setHasInitError(true);
            return [];
          });
        }),
      );
      setInitilizedPlugins(pluginsResult);
      setAreInitilalized(true);
    };

    if (isVisibilityLoaded && visiblePlugins.length > 0) {
      initPlugins();
    }
  }, [visiblePlugins, gwContext, isVisibilityLoaded]);

  useEffect(() => {
    setAreFiltered(false);

    const filterResults = async () => {
      setHasFilterError(false);

      const filteredResults = await Promise.all(
        initilizedPlugins.map((filterFn) => {
          return filterFn(searchContent).catch(() => {
            setHasFilterError(true);
            return [];
          });
        }),
      );

      setFilteredResults(filteredResults.flat());
      setAreFiltered(true);
    };

    filterResults();
  }, [searchContent, initilizedPlugins, hasVisibilityError, hasInitError]);

  return {
    areLoaded,
    results: filteredResults,
    isAnyPluginVisible,
    hasError,
    reload,
  };
};
